/**
 * Paragraph Style History Service
 *
 * Provides custom tracking for paragraph style changes independent of Word's
 * tracked changes API. This service maintains a persistent audit trail of all
 * style modifications throughout a styling session.
 *
 * Key features:
 * - trackedChangeCount never decreases (represents total changes made)
 * - isPending flag explicitly tracks review state
 * - styleHistory provides full audit trail of all changes
 *
 * @see docs/requirements/review-paragraphs/custom-tracking-changes.md
 */

import type { ClassificationLine, ClassificationResponse } from "src/types";

// ────────────────────────────────────────────────────────────────────────────
// Interfaces
// ────────────────────────────────────────────────────────────────────────────

/** Source of a style change event. */
export type StyleChangeSource = "initial" | "classifier" | "accept" | "reject" | "restyle";

/**
 * Represents a single style change event for audit/history purposes.
 */
export interface StyleChangeEntry {
  /** Style before the change. */
  previousStyle: string | null;
  /** Style after the change. */
  newStyle: string;
  /** Source of the change. */
  source: StyleChangeSource;
  /** ISO timestamp when the change occurred. */
  timestamp: string;
}

/**
 * Tracks paragraph style history for a document session.
 * Persists change records independent of Word's tracked changes state.
 */
export interface ParagraphStyleRecord {
  /** Document-order index (matches ParagraphInfo.index). */
  index: number;
  /** Session-scoped unique ID (matches ParagraphInfo.uniqueLocalId). */
  uniqueLocalId: string;
  /** Persistent content control tag (mss:p:<UUID>) — survives document reopen. Used to remap stale uniqueLocalId on session resume. Empty string for records created before this field was added. */
  paragraphTag: string;
  /** Original style when first captured (before classifier ran). */
  originalStyle: string;
  /** Current applied style (updated after each change). */
  currentStyle: string;
  /** Total number of style changes applied to this paragraph (never decreases). */
  trackedChangeCount: number;
  /** True if a style change is pending review; false if reviewed/applied. */
  isPending: boolean;
  /** True if the backend marked this paragraph as locked (maintain-existing-styles). Character style changes are skipped for locked paragraphs. */
  locked: boolean;
  /** Full history of style changes for audit trail. */
  styleHistory: StyleChangeEntry[];
}

// ────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ────────────────────────────────────────────────────────────────────────────

/** Returns the current ISO timestamp. */
function getTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Creates a new StyleChangeEntry.
 */
function createStyleChangeEntry(
  previousStyle: string,
  newStyle: string,
  source: StyleChangeSource
): StyleChangeEntry {
  return {
    previousStyle,
    newStyle,
    source,
    timestamp: getTimestamp(),
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Public Functions
// ────────────────────────────────────────────────────────────────────────────

/**
 * Creates initial style records from extracted document lines.
 *
 * Called before the classifier runs to capture the original state of
 * each paragraph. All records start with:
 * - isPending = false (no pending changes yet)
 * - trackedChangeCount = 0 (no changes made yet)
 * - styleHistory contains one 'initial' entry
 *
 * @param lines - Classification lines extracted from the document
 * @returns Array of ParagraphStyleRecord with initial state
 */
export function createStyleRecords(
  lines: ClassificationLine[],
  localIdToTag?: Map<string, string>
): ParagraphStyleRecord[] {
  return lines.map((line) => ({
    index: line.index,
    uniqueLocalId: line.paragraph_id ?? `temp-${line.index}`,
    paragraphTag: localIdToTag?.get(line.paragraph_id ?? "") ?? "",
    originalStyle: line.raw_style,
    currentStyle: line.raw_style,
    trackedChangeCount: 0,
    isPending: false,
    locked: false,
    styleHistory: [
      createStyleChangeEntry(line.raw_style, line.raw_style, "initial"),
    ],
  }));
}

/**
 * Updates style records after the classifier returns results.
 *
 * For each paragraph where the classifier assigned a different style:
 * - currentStyle is updated to the classified style
 * - trackedChangeCount is incremented
 * - isPending is set to true
 * - A 'classifier' entry is added to styleHistory
 *
 * Paragraphs where the style didn't change remain unchanged.
 *
 * @param records - Current style records (from createStyleRecords)
 * @param response - Classification response from the API
 * @returns New array of updated ParagraphStyleRecord
 */
export function updateRecordsFromClassification(
  records: ParagraphStyleRecord[],
  response: ClassificationResponse
): ParagraphStyleRecord[] {
  // Build a map of classified lines by index for O(1) lookup
  const classifiedLineMap = new Map(response.lines.map((line) => [line.index, line]));

  return records.map((record) => {
    const classifiedLine = classifiedLineMap.get(record.index);
    const classifiedStyle = classifiedLine?.style;
    const isLocked = classifiedLine?.locked ?? false;

    // If no classification result or style unchanged, return as-is (but carry locked flag)
    if (classifiedStyle === undefined || classifiedStyle === record.currentStyle) {
      return { ...record, locked: isLocked };
    }

    // Style changed — update the record
    return {
      ...record,
      locked: isLocked,
      currentStyle: classifiedStyle,
      trackedChangeCount: record.trackedChangeCount + 1,
      isPending: true,
      styleHistory: [
        ...record.styleHistory,
        createStyleChangeEntry(record.currentStyle, classifiedStyle, "classifier"),
      ],
    };
  });
}

/**
 * Marks paragraphs as accepted (approved).
 *
 * Sets isPending = false for the specified paragraphs.
 * trackedChangeCount is NOT incremented (acceptance is not a style change).
 * An 'accept' entry is added to styleHistory.
 *
 * @param records - Current style records
 * @param ids - Array of uniqueLocalId values to mark as accepted
 * @returns New array of updated ParagraphStyleRecord
 */
export function markAsAccepted(
  records: ParagraphStyleRecord[],
  ids: string[]
): ParagraphStyleRecord[] {
  const idSet = new Set(ids);

  return records.map((record) => {
    if (!idSet.has(record.uniqueLocalId)) {
      return record;
    }

    // Skip if already not pending
    if (!record.isPending) {
      return record;
    }

    return {
      ...record,
      isPending: false,
      styleHistory: [
        ...record.styleHistory,
        createStyleChangeEntry(record.currentStyle, record.currentStyle, "accept"),
      ],
    };
  });
}

/**
 * Marks paragraphs as rejected.
 *
 * Reverts the style to the previous style (from revertedStyles map).
 * trackedChangeCount is incremented (rejection is a style change).
 * isPending is set to false.
 * A 'reject' entry is added to styleHistory.
 *
 * @param records - Current style records
 * @param ids - Array of uniqueLocalId values to mark as rejected
 * @param revertedStyles - Map of uniqueLocalId → reverted style name
 * @returns New array of updated ParagraphStyleRecord
 */
export function markAsRejected(
  records: ParagraphStyleRecord[],
  ids: string[]
): ParagraphStyleRecord[] {
  const idSet = new Set(ids);

  return records.map((record) => {
    if (!idSet.has(record.uniqueLocalId)) {
      return record;
    }

    const revertedStyle = record.originalStyle;
    const previousStyle = record.currentStyle;

    return {
      ...record,
      currentStyle: revertedStyle,
      isPending: false,
      styleHistory: [
        ...record.styleHistory,
        createStyleChangeEntry(previousStyle, revertedStyle, "reject"),
      ],
    };
  });
}

/**
 * Marks paragraphs as restyled (manual style change).
 *
 * Updates the style to the new user-selected style.
 * trackedChangeCount is incremented.
 * isPending is set to false (restyle auto-approves).
 * A 'restyle' entry is added to styleHistory.
 *
 * @param records - Current style records
 * @param ids - Array of uniqueLocalId values to mark as restyled
 * @param newStyle - The new style applied by the user
 * @returns New array of updated ParagraphStyleRecord
 */
export function markAsRestyled(
  records: ParagraphStyleRecord[],
  ids: string[],
  newStyle: string
): ParagraphStyleRecord[] {
  const idSet = new Set(ids);

  return records.map((record) => {
    if (!idSet.has(record.uniqueLocalId)) {
      return record;
    }

    const previousStyle = record.currentStyle;

    // Skip if style is the same (no actual change)
    if (previousStyle === newStyle) {
      return {
        ...record,
        isPending: false,
      };
    }

    return {
      ...record,
      currentStyle: newStyle,
      trackedChangeCount: 0,
      isPending: false,
      styleHistory: [
        ...record.styleHistory,
        createStyleChangeEntry(previousStyle, newStyle, "restyle"),
      ],
    };
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Query Helpers
// ────────────────────────────────────────────────────────────────────────────

/**
 * Gets a single record by its uniqueLocalId.
 *
 * @param records - Current style records
 * @param id - The uniqueLocalId to find
 * @returns The matching record, or undefined if not found
 */
export function getRecordById(
  records: ParagraphStyleRecord[],
  id: string
): ParagraphStyleRecord | undefined {
  return records.find((r) => r.uniqueLocalId === id);
}

/**
 * Gets the count of paragraphs with pending changes.
 *
 * @param records - Current style records
 * @returns Number of records where isPending = true
 */
export function getPendingCount(records: ParagraphStyleRecord[]): number {
  return records.filter((r) => r.isPending).length;
}

/**
 * Gets the count of paragraphs that have been modified.
 *
 * @param records - Current style records
 * @returns Number of records where trackedChangeCount > 0
 */
export function getChangedCount(records: ParagraphStyleRecord[]): number {
  return records.filter((r) => r.trackedChangeCount > 0).length;
}

/**
 * Gets all records that have pending changes.
 *
 * @param records - Current style records
 * @returns Array of records where isPending = true
 */
export function getPendingRecords(records: ParagraphStyleRecord[]): ParagraphStyleRecord[] {
  return records.filter((r) => r.isPending);
}

/**
 * Gets all records that have been modified.
 *
 * @param records - Current style records
 * @returns Array of records where trackedChangeCount > 0
 */
export function getChangedRecords(records: ParagraphStyleRecord[]): ParagraphStyleRecord[] {
  return records.filter((r) => r.trackedChangeCount > 0);
}

/**
 * Re-maps stale uniqueLocalId values in deserialized records using the
 * persistent paragraphTag as the stable join key.
 *
 * Called on session resume after loadStyleRecords(), before setStyleRecords().
 * Records with an empty paragraphTag (pre-migration XML) or tags not found in
 * the current map (deleted paragraphs) are left unchanged.
 *
 * @param records - Deserialized records (may have stale uniqueLocalId)
 * @param tagToLocalId - Current session's tag → uniqueLocalId mapping from getTagToLocalIdMap()
 * @returns New array with uniqueLocalId updated to current session values where possible
 */
export function remapStaleRecords(
  records: ParagraphStyleRecord[],
  tagToLocalId: Map<string, string>
): ParagraphStyleRecord[] {
  return records.map((record) => {
    if (!record.paragraphTag) return record;
    const currentLocalId = tagToLocalId.get(record.paragraphTag);
    if (!currentLocalId || currentLocalId === record.uniqueLocalId) return record;
    return { ...record, uniqueLocalId: currentLocalId };
  });
}
